import { useMemo, useState } from "react";
import { Marker } from "react-leaflet";

import { usePois } from "src/hooks";
import { CalculationResult, Selection, type Poi } from "src/types";
import { wktPointToLatlng } from "src/utils/geometry.utils";
import { getLeafletMarkerIcon } from "src/utils/marker.utils";
import { getScenarios, getSubstances } from "src/utils/case-result.utils";
import { PoiResult } from "../PoiResult";
import { Dialog, DialogContent, DialogTitle, IconButton, styled, Typography, Zoom } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

interface Props {
  caseId: string | number;
  active: boolean;
  selection: Selection;
  calculationResults: CalculationResult[];
  ospmEnabled: boolean;
}
export function CaseResultPoisOnMap(props: Props) {
  const { selection, ospmEnabled, calculationResults } = props;
  const { t } = useTranslation();
  const { data: pois } = usePois(props.caseId);
  const [selectedPoi, setSelectedPoi] = useState<Poi | null>(null);

  /**
   * Memo
   */
  const scenarios = useMemo(() => {
    if (!calculationResults) return [];
    return getScenarios(calculationResults);
  }, [calculationResults]);

  const substances = useMemo(() => {
    if (!calculationResults) return [];
    return getSubstances(calculationResults, selection);
  }, [calculationResults, selection]);
  // render the component
  return render();

  function render() {
    if (!pois) return null;
    if (!props.active) return null;

    return (
      <>
        {pois.map((poi) => {
          const { lat, lng } = wktPointToLatlng(poi.point_frontendSrid);
          return (
            <Marker
              key={poi.id}
              position={[lat, lng]}
              icon={getLeafletMarkerIcon("default")}
              eventHandlers={{
                click: () => setSelectedPoi(poi),
              }}
            />
          );
        })}
        {selectedPoi && (
          <Styles open={!!selectedPoi} onClose={() => setSelectedPoi(null)} maxWidth="lg" TransitionComponent={Zoom}>
            <DialogTitle>
              <Typography variant="h1" component="h4">
                {t("label.poiTimeseries")}
              </Typography>
              <IconButton onClick={() => setSelectedPoi(null)}>
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <PoiResult poi={selectedPoi} scenarios={scenarios} substances={substances} ospmEnabled={ospmEnabled} />
            </DialogContent>
          </Styles>
        )}
      </>
    );
  }
}

const Styles = styled(Dialog)`
  .MuiDialogTitle-root {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;
